import { useState, useEffect, useMemo } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Search, ChevronLeft, ChevronRight, BookOpen, Loader2, Sparkles, Flag } from "lucide-react";
import {
  useQuestions,
  useCategories,
  useGenerateExplanation,
} from "@/hooks/use-questions";
import { useLanguage } from "@/hooks/use-language";
import { createErrorMessage } from "@/lib/error-message";
import { getQuestionText, getChoiceText, getExplanation } from "@/lib/localize";
import type { Question } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Markdown } from "@/components/ui/markdown";
import { CategorySelect } from "@/components/category-select";
import { ReportDialog } from "@/components/report-dialog";
import { renderQuestionHtml } from "@/lib/render-question-html";
import { isSafeImageUrl } from "@/lib/safe-url";

export const Route = createFileRoute("/_main/exams/$slug/questions")({
  component: QuestionsPage,
});

export function QuestionsPage() {
  const params = useParams({ strict: false }) as { slug: string };
  const examSlug = params.slug;
  const { t, locale } = useLanguage();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [numberInput, setNumberInput] = useState("");
  const [questionNumber, setQuestionNumber] = useState<number | undefined>(undefined);
  const [pageJumpInput, setPageJumpInput] = useState("");
  const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const perPage = 12;

  const { data, isLoading: loading, error } = useQuestions(
    examSlug,
    page,
    perPage,
    categoryId,
    search,
    questionNumber != null
      ? { fromDisplayNumber: questionNumber, toDisplayNumber: questionNumber }
      : undefined,
  );
  const { data: categories } = useCategories(examSlug);
  const generateExplanationMutation = useGenerateExplanation(examSlug);
  const generatingExplanation = generateExplanationMutation.isPending;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput || undefined);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounce question number input
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = numberInput.trim();
      if (!trimmed) {
        setQuestionNumber(undefined);
      } else {
        const n = Number(trimmed);
        setQuestionNumber(Number.isFinite(n) && n > 0 ? n : undefined);
      }
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [numberInput]);

  const questions = data?.questions ?? [];
  const meta = data?.meta;
  const totalPages = meta?.total_pages ?? 1;

  async function handleGenerateExplanation() {
    if (!selectedQuestion) return;
    try {
      const updated = await generateExplanationMutation.mutateAsync(
        selectedQuestion.id,
      );
      if (updated.explanation) {
        setSelectedQuestion({ ...selectedQuestion, explanation: updated.explanation });
      }
    } catch {
      // silently fail
    }
  }

  const categoryMap = useMemo(() => {
    const map = new Map<number, string>();
    if (categories) {
      for (const cat of categories) {
        map.set(cat.id, cat.name);
      }
    }
    return map;
  }, [categories]);

  function handleCategoryChange(value: string | null) {
    if (!value || value === "all") {
      setCategoryId(undefined);
    } else {
      setCategoryId(Number(value));
    }
    setPage(1);
  }

  function openQuestion(question: Question) {
    setSelectedQuestion(question);
    setDialogOpen(true);
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
            {t("questions.title")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
            {t("questions.desc")}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              placeholder={t("questions.search_placeholder")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">
              {t("questions.search_by_number")}
            </span>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              placeholder={t("questions.search_by_number_placeholder")}
              value={numberInput}
              onChange={(e) => setNumberInput(e.target.value)}
              className="w-24"
            />
          </div>
          <CategorySelect
            categories={categories}
            value={categoryId ? String(categoryId) : "all"}
            onValueChange={handleCategoryChange}
            placeholder={t("questions.select_category")}
            allLabel={t("questions.all_categories")}
            triggerClassName="w-full sm:w-[200px]"
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
            <span className="ml-2 text-zinc-500">{t("questions.loading")}</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {t("common.error")}: {createErrorMessage(error)}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && questions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
            <BookOpen className="mb-4 h-12 w-12" />
            <p className="text-lg font-medium">{t("questions.no_results")}</p>
            <p className="text-sm">{t("questions.no_results_desc")}</p>
          </div>
        )}

        {/* Question Grid */}
        {!loading && !error && questions.length > 0 && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {questions.map((question) => (
                <Card
                  key={question.id}
                  className="cursor-pointer transition-shadow hover:shadow-md"
                  onClick={() => openQuestion(question)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold">
                        {t("questions.question_num")}{question.display_number ?? question.question_number}
                      </CardTitle>
                      {question.is_multiple && (
                        <Badge variant="secondary" className="text-xs">
                          {t("common.multiple_choice")}
                        </Badge>
                      )}
                    </div>
                    {question.category && (
                      <Badge variant="outline" className="mt-1 w-fit text-xs">
                        {question.category.name}
                      </Badge>
                    )}
                    {!question.category && question.category_id && categoryMap.has(question.category_id) && (
                      <Badge variant="outline" className="mt-1 w-fit text-xs">
                        {categoryMap.get(question.category_id)}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent>
                    <p className="line-clamp-3 text-sm text-zinc-600 dark:text-zinc-400">
                      {getQuestionText(question, locale).slice(0, 100)}
                      {getQuestionText(question, locale).length > 100 ? "..." : ""}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Button
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                {t("common.previous")}
              </Button>
              <span className="text-sm text-zinc-600 dark:text-zinc-400">
                {page} / {totalPages} {t("common.page")}
                {meta && (
                  <span className="ml-2 text-zinc-400">
                    ({t("common.total")} {meta.total}{t("common.questions")})
                  </span>
                )}
              </span>
              <Button
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t("common.next")}
                <ChevronRight className="h-4 w-4" />
              </Button>
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const n = Number(pageJumpInput);
                  if (Number.isFinite(n) && n >= 1 && n <= totalPages) {
                    setPage(n);
                    setPageJumpInput("");
                  }
                }}
              >
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={totalPages}
                  placeholder={t("questions.page_input_placeholder")}
                  value={pageJumpInput}
                  onChange={(e) => setPageJumpInput(e.target.value)}
                  className="w-20"
                  aria-label={t("questions.go_to_page")}
                />
                <Button type="submit" variant="outline" disabled={!pageJumpInput}>
                  {t("questions.go")}
                </Button>
              </form>
            </div>
          </>
        )}
      </div>

      {/* Question Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          {selectedQuestion && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {t("questions.question_num")}{selectedQuestion.display_number ?? selectedQuestion.question_number}
                  {selectedQuestion.is_multiple && (
                    <Badge variant="secondary" className="text-xs">
                      {t("common.multiple_choice")}
                    </Badge>
                  )}
                </DialogTitle>
                {selectedQuestion.category && (
                  <Badge variant="outline" className="w-fit text-xs">
                    {selectedQuestion.category.name}
                  </Badge>
                )}
              </DialogHeader>

              <div className="mt-4 space-y-4">
                {/* Question text -- server-seeded content */}
                <div className="rounded-lg bg-zinc-50 p-4 dark:bg-zinc-900">
                  <div
                    className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800 dark:text-zinc-200 [&_strong]:font-bold"
                    dangerouslySetInnerHTML={{
                      __html: renderQuestionHtml(getQuestionText(selectedQuestion, locale)),
                    }}
                  />
                  {isSafeImageUrl(selectedQuestion.image_url) && (
                    <div className="mt-3">
                      <img
                        src={selectedQuestion.image_url}
                        alt={`${t("common.question")} ${selectedQuestion.display_number ?? selectedQuestion.question_number}`}
                        className="max-w-full rounded-lg border border-zinc-200 dark:border-zinc-700"
                      />
                    </div>
                  )}
                </div>

                {/* English text if available (only show when locale is ko and text_en exists) */}
                {locale === "ko" && selectedQuestion.text_en && (
                  <div className="rounded-lg border border-dashed border-zinc-200 p-4 dark:border-zinc-700">
                    <p className="mb-1 text-xs font-medium text-zinc-400">
                      English
                    </p>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                      {selectedQuestion.text_en}
                    </p>
                  </div>
                )}

                {/* Korean text if viewing in English and text_ko differs */}
                {locale === "en" && selectedQuestion.text_en && (
                  <div className="rounded-lg border border-dashed border-zinc-200 p-4 dark:border-zinc-700">
                    <p className="mb-1 text-xs font-medium text-zinc-400">
                      한국어
                    </p>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                      {selectedQuestion.text_ko}
                    </p>
                  </div>
                )}

                {/* Choices */}
                {selectedQuestion.choices && selectedQuestion.choices.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      {t("common.choices")}
                    </p>
                    {selectedQuestion.choices.map((choice) => (
                      <div
                        key={choice.label}
                        className={`rounded-lg border p-3 text-sm ${
                          choice.is_correct
                            ? "border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-950 dark:text-green-300"
                            : "border-zinc-200 bg-white text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                        }`}
                      >
                        <span className="mr-2 font-semibold">{choice.label}.</span>
                        {getChoiceText(choice, locale)}
                        {choice.is_correct && (
                          <Badge className="ml-2 bg-green-600 text-xs text-white">
                            {t("common.correct")}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Explanation */}
                {getExplanation(selectedQuestion, locale) ? (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
                    <p className="mb-1 text-xs font-medium text-blue-600 dark:text-blue-400">
                      {t("common.explanation")}
                    </p>
                    <div className="text-sm leading-relaxed text-blue-800 dark:text-blue-200">
                      <Markdown>{getExplanation(selectedQuestion, locale)!}</Markdown>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950"
                    onClick={handleGenerateExplanation}
                    disabled={generatingExplanation}
                  >
                    {generatingExplanation ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("questions.ai_explanation_loading")}
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        {t("questions.ai_explanation")}
                      </>
                    )}
                  </Button>
                )}

                {/* Report button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full cursor-pointer text-zinc-400 hover:text-red-500"
                  onClick={() => setReportDialogOpen(true)}
                >
                  <Flag className="mr-2 h-4 w-4" />
                  {t("report.button")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      {selectedQuestion && (
        <ReportDialog
          examSlug={examSlug}
          questionNumber={selectedQuestion.question_number}
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
        />
      )}
    </div>
  );
}
